import React from 'react';
import { Tag, Typography } from 'antd';
import {
  PRIMITIVE_AUTHORITY_FLAGS,
  type PrimitivePresentationState,
} from './primitiveSurfaceState';
import { PrimitiveSurfaceFrame } from './PrimitiveSurfaceFrame';
import styles from './primitives.module.css';

const { Text, Paragraph } = Typography;

export const APPROVAL_SUMMARY_DECISION_STATUSES_V0 = [
  'pending',
  'approved',
  'rejected',
  'withdrawn',
] as const;

export type ApprovalSummaryDecisionStatusV0 =
  (typeof APPROVAL_SUMMARY_DECISION_STATUSES_V0)[number];

/** Server-recorded decision snapshot — this primitive never itself decides. */
export interface ApprovalDecisionStateV0 {
  readonly status: ApprovalSummaryDecisionStatusV0;
  readonly decidedByLabel?: string | null;
  readonly decidedAt?: string | null;
  readonly reason?: string | null;
}

export interface ApprovalSummaryProps {
  readonly presentationState: PrimitivePresentationState;
  readonly decision?: ApprovalDecisionStateV0 | null;
  readonly decisionAuthorityLabel?: string | null;
  /**
   * Frozen invariant APPROVE_DENY_DECISION_AUTHORITY=EQUAL: true only when the
   * hosting page renders Approve/Reject with equal visual weight (same size,
   * both always visible when decidable, neither hidden). This primitive does
   * not render the decision buttons itself — it reflects and documents the
   * equality claim made by the page that owns them.
   */
  readonly equalDecisionAuthorityRendered?: boolean;
  readonly grantsAuthority?: false;
  readonly grantsApproval?: false;
  readonly 'data-testid'?: string;
}

const STATUS_LABEL_KO: Record<ApprovalSummaryDecisionStatusV0, string> = {
  pending: '결정 대기',
  approved: '승인됨',
  rejected: '반려됨',
  withdrawn: '철회됨',
};

const STATUS_TAG_COLOR: Record<ApprovalSummaryDecisionStatusV0, string> = {
  pending: 'gold',
  approved: 'green',
  rejected: 'red',
  withdrawn: 'default',
};

/**
 * ApprovalSummary (Family B) — decision-authority summary for approval
 * surfaces. Encodes two frozen invariants:
 *  - APPROVE_DENY_DECISION_AUTHORITY=EQUAL (documented via
 *    equalDecisionAuthorityRendered; this primitive never grants approval).
 *  - APPROVED ≠ EXECUTED (an approved decision is not, by itself, proof that
 *    any downstream mutation executed — that remains ReceiptLink's job).
 */
export function ApprovalSummary(props: ApprovalSummaryProps): React.ReactElement {
  const grantsAuthority = props.grantsAuthority ?? PRIMITIVE_AUTHORITY_FLAGS.grantsAuthority;
  const grantsApproval = props.grantsApproval ?? PRIMITIVE_AUTHORITY_FLAGS.grantsApproval;
  if (grantsAuthority !== false || grantsApproval !== false) {
    throw new Error('ApprovalSummary must keep grantsAuthority/grantsApproval=false');
  }

  const decision = props.decision;
  const equalDecisionAuthorityRendered = props.equalDecisionAuthorityRendered ?? false;

  return (
    <PrimitiveSurfaceFrame
      title="승인 요약"
      presentationState={props.presentationState}
      emptyMessage="승인 결정 정보가 없습니다."
      data-testid={props['data-testid'] ?? 'approval-summary'}
      aria-label="승인 요약"
    >
      <div
        className={styles.primitiveStack}
        data-equal-decision-authority={String(equalDecisionAuthorityRendered)}
      >
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          grantsApproval=false · 승인/반려는 동일한 결정 권위를 가집니다
        </Paragraph>
        {decision ? (
          <>
            <div className={styles.primitiveFieldRow}>
              <span className={styles.primitiveFieldLabel}>상태</span>
              <Tag color={STATUS_TAG_COLOR[decision.status]}>
                {STATUS_LABEL_KO[decision.status]}
              </Tag>
            </div>
            {props.decisionAuthorityLabel ? (
              <div className={styles.primitiveFieldRow}>
                <span className={styles.primitiveFieldLabel}>결정 권한</span>
                <Text>{props.decisionAuthorityLabel}</Text>
              </div>
            ) : null}
            {decision.decidedByLabel ? (
              <div className={styles.primitiveFieldRow}>
                <span className={styles.primitiveFieldLabel}>결정자</span>
                <Text>{decision.decidedByLabel}</Text>
              </div>
            ) : null}
            {decision.decidedAt ? (
              <div className={styles.primitiveFieldRow}>
                <span className={styles.primitiveFieldLabel}>결정 일시</span>
                <Text>{decision.decidedAt}</Text>
              </div>
            ) : null}
            {decision.reason ? (
              <div className={styles.primitiveFieldRow}>
                <span className={styles.primitiveFieldLabel}>사유</span>
                <Text>{decision.reason}</Text>
              </div>
            ) : null}
            {decision.status === 'approved' ? (
              <Text type="secondary" data-testid="approval-summary-approved-ne-executed">
                승인됨 ≠ 실행됨 — 실행 완료는 서버 영수증으로만 확인하세요.
              </Text>
            ) : null}
          </>
        ) : (
          <Text type="secondary">ApprovalDecisionStateV0 없음</Text>
        )}
      </div>
    </PrimitiveSurfaceFrame>
  );
}
